import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { ExtendedWarrantyPdpRenderMethod } from "@redotech/redo-model/extended-warranty";
import { InsertionPosition } from "@redotech/redo-model/widget";
import { useMemo } from "react";
import { RedoExtendedWarrantyOptionsContainerElement } from "../pdp-options/element";
import { EwSettingsContext } from "../provider/ew-settings-provider";
import { useClickRefresh } from "./use-click-refresh";

const positionMap: Record<InsertionPosition, InsertPosition> = {
  [InsertionPosition.BEFORE]: "beforebegin",
  [InsertionPosition.AFTER]: "afterend",
  [InsertionPosition.PREPEND]: "afterbegin",
  [InsertionPosition.APPEND]: "beforeend",
};

type PdpPortal = {
  foundSelector?: string;
  portal: RedoExtendedWarrantyOptionsContainerElement;
};

export function useRenderPdps(): PdpPortal[] {
  const { getWidgetConfig } = useRequiredContext(EwSettingsContext);

  const widgetConfig = useMemo(() => {
    return getWidgetConfig();
  }, [getWidgetConfig]);

  const clickEvent = useClickRefresh({
    selector: widgetConfig.clickListenerSelector ?? "",
  });

  const renderPdpsLoad = useLoad(async () => {
    const pdps: PdpPortal[] = [];

    if (
      widgetConfig.pdpRenderMethod !==
      ExtendedWarrantyPdpRenderMethod.SCRIPT_TAG
    ) {
      return null;
    }
    const pdpSelector = widgetConfig.pdpSelector;
    if (!pdpSelector) {
      return null;
    }
    const containers = document.querySelectorAll(pdpSelector);

    for (const container of containers) {
      const pdpElement = document.createElement(
        RedoExtendedWarrantyOptionsContainerElement.TAG,
      ) as RedoExtendedWarrantyOptionsContainerElement;
      pdpElement.id = "redo-extended-warranty-options-container";
      const insertPosition = widgetConfig.pdpInsertPosition
        ? positionMap[widgetConfig.pdpInsertPosition]
        : "afterend";
      container.insertAdjacentElement(insertPosition, pdpElement);
      const matchingSelector = pdpSelector
        .split(",")
        .find((selector) => container.matches(selector));
      pdps.push({ foundSelector: matchingSelector, portal: pdpElement });
    }

    return pdps;
  }, [widgetConfig, clickEvent]);

  return renderPdpsLoad.value ?? [];
}
